import {
  DeprecatedDropdown,
  DeprecatedDropdownButton,
  DeprecatedDropdownItem,
  DeprecatedDropdownMenu,
} from "@deere/fuel-react";
import { Box, Portal } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { BusinessAction, RBACRole } from "../../../../api/rbac/types";
import { ConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../context/UserContext/UserContext";
import {
  CATEGORIES,
  TOGGLES,
} from "../../../../pages/featureToggles/constants";

interface OrdersAndTrackingMenuProps {
  dealerAccountId: string;
  selected?: boolean;
}

export const OrdersAndTrackingMenu = ({
  dealerAccountId,
  selected = false,
}: OrdersAndTrackingMenuProps) => {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const user = useContext(UserContext);
  const { isRBACEnabled } = useContext(RBACAccessContext);
  const { selectedAccounts } = useContext(DealerAccountContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const {
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE,
    ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE,
    ENABLE_HANDLING_UNITS,
    ENABLE_CREDIT_STATUS,
    JDPOINT_URL,
    ENABLE_JDPS_POSTSHIP_ACTIVITIES,
  } = useContext(ConfigContext);

  const [orderCreationHover, setOrderCreationHover] = useState(false);
  const [orderSearchHover, setOrderSearchHover] = useState(false);

  const orderCreationRef = useRef<HTMLDivElement>(null);
  const orderSearchRef = useRef<HTMLDivElement>(null);
  const base64AccountId = btoa(dealerAccountId);

  const isMultipleAccountSelected = selectedAccounts?.length > 1;

  const isAuthorizedToViewOrders =
    !isRBACEnabled || user?.isAuthorizedTo(BusinessAction.VIEW_ORDERS);
  const isAuthorizedToCreateAndEditOrders =
    !isRBACEnabled ||
    user?.isAuthorizedTo(BusinessAction.CREATE_ORDER) ||
    user?.isAuthorizedTo(BusinessAction.EDIT_ORDER);
  const isAuthorizedToViewHandlingUnits =
    !isRBACEnabled || user?.isAuthorizedTo(BusinessAction.VIEW_HANDLING_UNITS);
  const isAuthorizedToViewCreditStatus =
    !isRBACEnabled || user?.isAuthorizedTo(BusinessAction.VIEW_CREDIT_STATUS);

  const isOrdersFeatureEnabled =
    !ENABLE_FEATURE_TOGGLE_CONTEXT_V2 ||
    isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.ENABLE_ORDERS);

  const disableEmergencyOrderFeatureToggle =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.DISABLE_EMERGENCY_ORDER);

  const disableStockOrderFeatureToggle =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.DISABLE_STOCK_ORDER);

  const enablePromotionOrderTypeFeatureToggle =
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
    isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.ENABLE_PROMOTION_ORDER) &&
    ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE;

  const isCreditStatusEnabled =
    ENABLE_CREDIT_STATUS &&
    isAuthorizedToViewCreditStatus &&
    (!ENABLE_FEATURE_TOGGLE_CONTEXT_V2 ||
      isToggleEnabled(CATEGORIES.EXTREME_UI, TOGGLES.CREDIT_STATUS));

  const isHandlingUnitsFeatureToggleEnabled =
    !(ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && isRBACEnabled) ||
    isToggleEnabled(
      CATEGORIES.HANDLING_UNIT_SERVICE,
      TOGGLES.ENABLE_HANDLING_UNITS
    );

  const isJdpsDealerBlockedByToggle =
    user?.is(RBACRole.JDPS_SERVICE_DEALER) &&
    ENABLE_JDPS_POSTSHIP_ACTIVITIES &&
    !isToggleEnabled(
      CATEGORIES.RETURNS,
      TOGGLES.ENABLE_JDPS_POSTSHIP_ACTIVITIES
    );

  const isHandlingUnitsEnabled =
    ENABLE_HANDLING_UNITS &&
    isAuthorizedToViewHandlingUnits &&
    isHandlingUnitsFeatureToggleEnabled &&
    !isJdpsDealerBlockedByToggle;

  const isAllBackordersEnabled = useMemo(() => {
    if (ENABLE_FEATURE_TOGGLE_CONTEXT_V2) {
      return isToggleEnabled(
        CATEGORIES.EXTREME_UI,
        TOGGLES.ENABLE_JDPOINT_ALL_BACKORDERS
      );
    }
    return false;
  }, [dealerAccountId]);
  const isStockOrderEnabled = ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE
    ? isMultipleAccountSelected || !disableStockOrderFeatureToggle
    : !disableStockOrderFeatureToggle;

  const isEmergencyOrderEnabled = ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE
    ? isMultipleAccountSelected || !disableEmergencyOrderFeatureToggle
    : !disableEmergencyOrderFeatureToggle;

  const isPromotionOrderEnabled = ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE
    ? (isMultipleAccountSelected && enablePromotionOrderTypeFeatureToggle) ||
      enablePromotionOrderTypeFeatureToggle
    : enablePromotionOrderTypeFeatureToggle;

  const getOrderCreationUrl = (
    orderType: "emergency" | "stock" | "promotion"
  ) => {
    if (ENABLE_ACCOUNT_SELECTION_ON_ORDER_CREATE && isMultipleAccountSelected) {
      return `/orders/${orderType}/select-account`;
    }

    return `/orders/create/${orderType}`;
  };

  const creditStatusUrl = `/credit-status`;

  const allOrdersURL = `/orders`;

  const ordersByLineItemsURL = `/orders/line-items`;

  const allBackordersURL = `${JDPOINT_URL}/dns/searchForBackorders.con?accountID=${base64AccountId}&isCallFromPITM=true`;

  const handlingUnitsURL = `/handlingUnits`;

  const dropDownItemStyle = {
    borderWidth: "0px 0px 0px 3px",
    borderStyle: "solid",
    borderColor: "transparent",
  };

  const dropDownHoverStyle = {
    ".MenuItem-link:hover": {
      color: theme.tokens.FuelColorBrandPrimary,
      backgroundColor: theme.tokens.FuelColorAlertSuccessBackground,
      borderColor: `${theme.tokens.FuelColorAlertSuccessBorder} !important`,
    },
  };

  const hasAnyItemEnabled =
    isCreditStatusEnabled ||
    (isOrdersFeatureEnabled &&
      isAuthorizedToCreateAndEditOrders &&
      (isStockOrderEnabled ||
        isEmergencyOrderEnabled ||
        isPromotionOrderEnabled)) ||
    (isOrdersFeatureEnabled && isAuthorizedToViewOrders) ||
    isHandlingUnitsEnabled;

  if (!hasAnyItemEnabled) {
    return null;
  }

  return (
    <DeprecatedDropdown>
      <DeprecatedDropdownButton
        variant="text"
        data-testid="orders-and-tracking-dropdown"
        style={{
          padding: "20px 8px",
          color: theme.tokens.FuelColorGray600,
          borderRadius: "0px",
          ...(selected && {
            color: theme.tokens.FuelColorBrandPrimary,
            fontWeight: theme.tokens.FuelFontWeightSemibold,
            borderBottom: `3px solid ${theme.tokens.FuelColorBrandPrimary}`,
          }),
        }}
      >
        {t("header.navItem.ordersAndTracking")}
      </DeprecatedDropdownButton>
      <DeprecatedDropdownMenu
        style={{ minWidth: "max-content", overflow: "visible" }}
      >
        <style>
          {`
            .orders-and-tracking-menu .MenuItem-link {
              text-decoration: none;
            }
          `}
        </style>
        <Box
          className="orders-and-tracking-menu"
          sx={{ display: "flex", position: "relative" }}
        >
          <Box
            sx={{
              ...dropDownHoverStyle,
              position: "relative",
              overflow: "visible",
            }}
          >
            {isCreditStatusEnabled && (
              <Link to={creditStatusUrl} style={{ textDecoration: "none" }}>
                <DeprecatedDropdownItem
                  data-testid="orders-tracking-credit-status"
                  style={dropDownItemStyle}
                >
                  {t("header.navItem.creditStatus")}
                </DeprecatedDropdownItem>
              </Link>
            )}
            {isOrdersFeatureEnabled &&
              isAuthorizedToCreateAndEditOrders &&
              (isStockOrderEnabled ||
                isEmergencyOrderEnabled ||
                isPromotionOrderEnabled) && (
                <Box
                  ref={orderCreationRef}
                  onMouseEnter={() => setOrderCreationHover(true)}
                  onMouseLeave={() => setOrderCreationHover(false)}
                  sx={{ position: "relative", overflow: "visible" }}
                >
                  <DeprecatedDropdownItem
                    data-testid="orders-tracking-order-creation"
                    style={{
                      ...dropDownItemStyle,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "default",
                      position: "relative",
                      width: "200px",
                      paddingRight: "24px",
                    }}
                  >
                    <span>{t("header.navItem.orders.createOrder")}</span>
                    <span style={{ position: "absolute", right: "12px" }}>
                      ›
                    </span>
                  </DeprecatedDropdownItem>
                </Box>
              )}
            {orderCreationHover && orderCreationRef.current && (
              <Portal>
                <Box
                  onMouseEnter={() => setOrderCreationHover(true)}
                  onMouseLeave={() => setOrderCreationHover(false)}
                  sx={{
                    position: "fixed",
                    left: `${
                      orderCreationRef.current.getBoundingClientRect().right
                    }px`,
                    top: `${
                      orderCreationRef.current.getBoundingClientRect().top
                    }px`,
                    backgroundColor: theme.tokens.FuelColorWhite,
                    boxShadow: theme.tokens.FuelShadowMd,
                    minWidth: "200px",
                    zIndex: 10000,
                    "& .MenuItem-link:hover": {
                      color: theme.tokens.FuelColorBrandPrimary,
                      backgroundColor:
                        theme.tokens.FuelColorAlertSuccessBackground,
                      borderColor: `${theme.tokens.FuelColorAlertSuccessBorder} !important`,
                    },
                  }}
                >
                  {isEmergencyOrderEnabled && (
                    <Link
                      to={getOrderCreationUrl("emergency")}
                      style={{ textDecoration: "none" }}
                    >
                      <DeprecatedDropdownItem
                        data-testid="order-creation-emergency"
                        style={dropDownItemStyle}
                      >
                        {t("header.navItem.orders.createOrder.emergencyOrder")}
                      </DeprecatedDropdownItem>
                    </Link>
                  )}

                  {isStockOrderEnabled && (
                    <Link
                      to={getOrderCreationUrl("stock")}
                      style={{ textDecoration: "none" }}
                    >
                      <DeprecatedDropdownItem
                        data-testid="order-creation-stock"
                        style={dropDownItemStyle}
                      >
                        {t("header.navItem.orders.createOrder.stockOrder")}
                      </DeprecatedDropdownItem>
                    </Link>
                  )}

                  {isPromotionOrderEnabled && (
                    <Link
                      to={getOrderCreationUrl("promotion")}
                      style={{ textDecoration: "none" }}
                    >
                      <DeprecatedDropdownItem
                        data-testid="order-creation-promotion"
                        style={dropDownItemStyle}
                      >
                        {t("header.navItem.orders.createOrder.promotionOrder")}
                      </DeprecatedDropdownItem>
                    </Link>
                  )}
                </Box>
              </Portal>
            )}
            {isOrdersFeatureEnabled && isAuthorizedToViewOrders && (
              <Box
                ref={orderSearchRef}
                onMouseEnter={() => setOrderSearchHover(true)}
                onMouseLeave={() => setOrderSearchHover(false)}
                sx={{ position: "relative", overflow: "visible" }}
              >
                <DeprecatedDropdownItem
                  data-testid="orders-tracking-order-search"
                  style={{
                    ...dropDownItemStyle,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "default",
                    position: "relative",
                    width: "200px",
                    paddingRight: "24px",
                  }}
                >
                  <span>{t("header.navItem.orders.searchOrder")}</span>
                  <span style={{ position: "absolute", right: "12px" }}>›</span>
                </DeprecatedDropdownItem>
              </Box>
            )}
            {orderSearchHover && orderSearchRef.current && (
              <Portal>
                <Box
                  onMouseEnter={() => setOrderSearchHover(true)}
                  onMouseLeave={() => setOrderSearchHover(false)}
                  sx={{
                    position: "fixed",
                    left: `${
                      orderSearchRef.current.getBoundingClientRect().right
                    }px`,
                    top: `${
                      orderSearchRef.current.getBoundingClientRect().top
                    }px`,
                    backgroundColor: theme.tokens.FuelColorWhite,
                    boxShadow: theme.tokens.FuelShadowMd,
                    minWidth: "200px",
                    zIndex: 10000,
                    "& .MenuItem-link:hover": {
                      color: theme.tokens.FuelColorBrandPrimary,
                      backgroundColor:
                        theme.tokens.FuelColorAlertSuccessBackground,
                      borderColor: `${theme.tokens.FuelColorAlertSuccessBorder} !important`,
                    },
                  }}
                >
                  <Link to={allOrdersURL} style={{ textDecoration: "none" }}>
                    <DeprecatedDropdownItem
                      data-testid="order-search-all-orders"
                      style={dropDownItemStyle}
                    >
                      {t("header.navItem.orders.searchOrder.allOrders")}
                    </DeprecatedDropdownItem>
                  </Link>

                  <Link
                    to={ordersByLineItemsURL}
                    style={{ textDecoration: "none" }}
                  >
                    <DeprecatedDropdownItem
                      data-testid="order-search-by-line-items"
                      style={dropDownItemStyle}
                    >
                      {t("header.navItem.orders.searchOrders.ordersByLineItem")}
                    </DeprecatedDropdownItem>
                  </Link>

                  {isAllBackordersEnabled && (
                    <a
                      href={allBackordersURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      <DeprecatedDropdownItem
                        data-testid="order-search-all-backorders"
                        style={dropDownItemStyle}
                      >
                        {t("header.navItem.orders.searchOrders.allBackorders")}
                      </DeprecatedDropdownItem>
                    </a>
                  )}
                </Box>
              </Portal>
            )}
            {isHandlingUnitsEnabled && (
              <Link to={handlingUnitsURL} style={{ textDecoration: "none" }}>
                <DeprecatedDropdownItem
                  data-testid="orders-tracking-handling-units"
                  style={dropDownItemStyle}
                >
                  {t("header.navItem.handlingunits")}
                </DeprecatedDropdownItem>
              </Link>
            )}
          </Box>
        </Box>
      </DeprecatedDropdownMenu>
    </DeprecatedDropdown>
  );
};
